import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { mediaUploadSaga } from "./redux/saga";
import { selectIsLoading, selectUploadProgress } from "./redux/selector";
import { mediaUploadReducer, mediaUploadSlice } from "./redux/slice";

interface MediaUploaderProps {
  onUpload: (file: File) => void;
  accept?: string;
  validateFile?: (file: File) => boolean;
  children: (props: {
    isLoading: boolean;
    isDragging: boolean;
    handleDragOver: (e: React.DragEvent) => void;
    handleDragLeave: (e: React.DragEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
  }) => React.ReactNode;
}
const Index: React.FC<MediaUploaderProps> = ({
  onUpload,
  accept = "image/*",
  validateFile,
  children,
}) => {
  useInjectReducer({ key: mediaUploadSlice, reducer: mediaUploadReducer });
  useInjectSaga({ key: mediaUploadSlice, saga: mediaUploadSaga });

  const isLoading = useSelector(selectIsLoading);
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const isFileValid = (file: File) =>
    validateFile ? validateFile(file) : file.type.startsWith("image/");

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && isFileValid(file)) {
      onUpload(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isFileValid(file)) {
      onUpload(file);
    }
    // Reset the input value to allow uploading the same file or retry after error
    e.target.value = "";
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileSelect}
      />
      {children({
        isLoading,
        isDragging,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleFileSelect,
        fileInputRef,
      })}
    </>
  );
};

export default Index;
